import { isAutomatedMessage } from '../shared/automated-messages.js';
export { isAutoReplyText } from '../shared/automated-messages.js';

export function escapeHtml(value = '') {
  return String(value).replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
}

export function idOf(value) {
  return String(value?.id || value?._id || value || '');
}

export function initials(value = '') {
  return String(value).split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]).join('').toUpperCase() || '?';
}

export function formatDate(value, withTime = false) {
  if (!value) return '—';
  const date = new Date(value);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const yesterday = new Date(now.getTime() - 86_400_000).toDateString() === date.toDateString();
  if (sameDay) return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  if (yesterday) return 'Ieri';
  return date.toLocaleDateString('it-IT', withTime ? { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' } : { day: '2-digit', month: 'short', year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric' });
}

export function formatMoney(value = 0, currency = 'EUR') {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: currency || 'EUR' }).format(Number(value || 0));
}

export function relativeTime(value) {
  if (!value) return '—';
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat('it', { numeric: 'auto' });
  const units = [[60, 'second'], [60, 'minute'], [24, 'hour'], [7, 'day'], [4.35, 'week'], [12, 'month'], [Infinity, 'year']];
  let duration = seconds;
  for (const [limit, unit] of units) {
    if (Math.abs(duration) < limit) return formatter.format(Math.round(duration), unit);
    duration /= limit;
  }
  return formatDate(value);
}

export function mailboxImportMetrics(account = {}, now = Date.now()) {
  const progress = account.importProgress || {};
  const scanned = Math.max(0, Number(progress.scanned) || 0);
  const imported = Math.max(0, Number(progress.imported) || 0);
  const ignored = Math.max(0, Number(progress.ignored) || 0);
  const totalEstimate = Math.max(0, Number(progress.totalEstimate) || 0);
  const percent = totalEstimate > 0 ? Math.min(100, Math.floor(scanned / totalEstimate * 100)) : null;
  const startedAt = progress.startedAt ? new Date(progress.startedAt).getTime() : NaN;
  const elapsedMs = Number(now) - startedAt;
  const remaining = Math.max(0, totalEstimate - scanned);
  const etaMs = Number.isFinite(startedAt) && elapsedMs >= 10_000 && scanned >= 100 && remaining > 0
    ? Math.round(remaining * elapsedMs / scanned)
    : null;
  return { scanned, imported, ignored, totalEstimate, percent, etaMs };
}

export function gorgiasMigrationMetrics(run = {}, now = Date.now()) {
  const phases = ['TEAM', 'TAG', 'USER', 'CUSTOMER', 'TICKET', 'MACRO', 'VIEW'];
  const counts = run.counts || {};
  const progress = run.progress || {};
  const entityType = phases.includes(progress.entityType) ? progress.entityType : phases.filter((phase) => counts[phase]).at(-1) || null;
  const phaseCounts = entityType ? counts[entityType] || {} : {};
  const processed = Math.max(0, Number(progress.processed ?? phaseCounts.processed) || 0);
  const skipped = Math.max(0, Number(progress.skipped ?? phaseCounts.skipped) || 0);
  const sourceTotal = Math.max(0, Number(progress.sourceTotal ?? phaseCounts.sourceTotal) || 0);
  const percent = sourceTotal > 0 ? Math.min(100, Math.floor((processed + skipped) / sourceTotal * 100)) : null;
  const totalProcessed = Object.values(counts).reduce((sum, value) => sum + Math.max(0, Number(value?.processed) || 0), 0);
  const totalSkipped = Object.values(counts).reduce((sum, value) => sum + Math.max(0, Number(value?.skipped) || 0), 0);
  const startedAt = progress.startedAt ? new Date(progress.startedAt).getTime() : NaN;
  const elapsedMs = Number(now) - startedAt;
  const completedInPhase = processed + skipped;
  const remaining = Math.max(0, sourceTotal - completedInPhase);
  const etaMs = Number.isFinite(startedAt) && elapsedMs >= 10_000 && completedInPhase >= 20 && remaining > 0
    ? Math.round(remaining * elapsedMs / completedInPhase)
    : null;
  return { entityType, phase: entityType ? phases.indexOf(entityType) + 1 : 0, phaseCount: phases.length, processed, skipped, sourceTotal, percent, totalProcessed, totalSkipped, etaMs };
}

export function debounce(callback, delay = 400) {
  let timeout;
  return (...args) => { clearTimeout(timeout); timeout = setTimeout(() => callback(...args), delay); };
}

export function textToHtml(value = '') {
  return `<p>${escapeHtml(value).replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
}

export function summaryMarkdownToHtml(value = '') {
  const inline = (line) => escapeHtml(line).replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  const lines = String(value || '').split(/\r?\n/);
  const output = [];
  let list = [];
  const flushList = () => {
    if (!list.length) return;
    output.push(`<ul>${list.map((item) => `<li>${inline(item)}</li>`).join('')}</ul>`);
    list = [];
  };
  for (const line of lines) {
    const bullet = line.match(/^\s*[-•]\s+(.+)$/);
    if (bullet) { list.push(bullet[1]); continue; }
    flushList();
    output.push(line.trim() ? `<p>${inline(line)}</p>` : '<br>');
  }
  flushList();
  return output.join('');
}

export function internalTrackingUrl(orderNumber = '') {
  const normalized = String(orderNumber || '').trim().replace(/^#+\s*/, '');
  return normalized ? `https://tracking.bottleofitaly.com/track/${encodeURIComponent(normalized)}` : null;
}

export function messagesNeedingTranslation(messages = [], language = '') {
  return messages.filter((message) => ['INBOUND_EMAIL', 'OUTBOUND_EMAIL'].includes(message.type) && !isAutomatedMessage(message) && !isTranslationComplete(message, language));
}

export function automaticTranslationCandidates(messages = [], language = 'it') {
  return messagesNeedingTranslation(messages, language);
}

export function isTranslationComplete(message = {}, language = 'it') {
  const detected = String(message.detectedLanguage || '');
  if (!detected) return false;
  if (detected === language) return true;
  if (detected === 'und') return message.translationStates?.[language]?.status === 'SKIPPED_NONLINGUISTIC';
  return Boolean((message.translations || {})[language]);
}

export function customerReplyLanguage(messages = [], fallback = '', supported = []) {
  const inbound = messages.filter((message) => message.type === 'INBOUND_EMAIL');
  if (!inbound.length) return supported.includes(fallback) ? fallback : '';
  const latest = inbound.at(-1);
  if (!latest.detectedLanguage) return '';
  if (latest.detectedLanguage !== 'und' && supported.includes(latest.detectedLanguage)) return latest.detectedLanguage;
  for (let index = inbound.length - 2; index >= 0; index -= 1) {
    const language = inbound[index].detectedLanguage;
    if (language && language !== 'und' && supported.includes(language)) return language;
  }
  return supported.includes(fallback) ? fallback : '';
}

export function translationPresentation(message = {}, language = 'it', job = {}) {
  if (!['INBOUND_EMAIL', 'OUTBOUND_EMAIL'].includes(message.type) || isAutomatedMessage(message)) return { phase: 'none', action: null };
  const translation = (message.translations || {})[language];
  const status = String(job.status || '').toLowerCase();
  if (['pending', 'enqueued', 'queued', 'running', 'retry_scheduled', 'reconnecting', 'failed'].includes(status)) {
    const durableStatus = status === 'failed' ? 'reconnecting' : status;
    return { phase: 'loading', action: job.mode === 'background' && durableStatus !== 'running' ? 'force' : null, mode: job.mode, status: durableStatus };
  }
  if (status === 'blocked') return { phase: 'blocked', action: 'retry', error: job.error || '' };
  if (translation) return { phase: 'translated', action: 'retranslate' };
  if (message.detectedLanguage === language || (message.detectedLanguage === 'und' && status === 'skipped_nonlinguistic')) return { phase: 'none', action: null };
  return { phase: 'idle', action: 'translate' };
}

export function translationProgress(messages = [], language = 'it', jobsByMessage = {}) {
  const eligible = messages.filter((message) => ['INBOUND_EMAIL', 'OUTBOUND_EMAIL'].includes(message.type) && !isAutomatedMessage(message) && !(message.detectedLanguage === 'und' && message.translationStates?.[language]?.status === 'SKIPPED_NONLINGUISTIC'));
  let completed = 0;
  let active = 0;
  let failed = 0;
  for (const message of eligible) {
    if (isTranslationComplete(message, language)) completed += 1;
    const status = String(jobsByMessage[idOf(message)]?.status || '').toLowerCase();
    if (['pending', 'enqueued', 'queued', 'running', 'retry_scheduled', 'reconnecting', 'failed'].includes(status)) active += 1;
    if (status === 'blocked') failed += 1;
  }
  return { completed, total: eligible.length, active, failed };
}

export { splitQuotedText } from '../shared/quoted-history.js';

export function lastMessageAt(ticket = {}) {
  const times = [ticket.lastCustomerMessageAt, ticket.lastAgentMessageAt]
    .filter(Boolean)
    .map((value) => new Date(value).getTime())
    .filter(Number.isFinite);
  if (!times.length) return ticket.createdAt || null;
  return new Date(Math.max(...times)).toISOString();
}

export function formatFormato(value) {
  if (value === null || value === undefined || value === '') return '';
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) return '';
  return parsed === 375 ? '375 ml' : `${parsed} cl`;
}

export function formatGradazione(value) {
  const parsed = Number(String(value ?? '').replace(',', '.'));
  if (!Number.isFinite(parsed) || parsed <= 0) return '';
  return `${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(parsed)}% vol.`;
}
